<script setup>
  import { computed, nextTick, ref } from 'vue';
  import Icon from '@/components/icons/Icon.vue';

  const inputValue = defineModel();

  const props = defineProps({
    id: {
      type: String,
      default: 'modal-text-input'
    },
    name: {
      type: String,
      default: 'modal-text-input'
    },
    labelText: {
      type: String,
      default: null
    },
    required: {
      type: Boolean,
      default: false
    },
    subText: {
      type: String,
      default: null
    },
    placeholder: {
      type: String,
      default: null
    },
    errorText: {
      type: String,
      default: null
    },
    valid: {
      type: Boolean,
      default: false
    },
    multi: {
      type: Boolean,
      default: false
    },
    minItems: {
      type: Number,
      default: 1
    },
    maxItems: {
      type: Number,
      default: Infinity
    }
  });

  if (import.meta.env.DEV) {
    if (props.multi && !Array.isArray(inputValue.value)) {
      console.warn(`[ModalTextInput] multi=true expects v-model to be an array, got ${typeof inputValue.value}`);
    } else if (!props.multi && Array.isArray(inputValue.value)) {
      console.warn('[ModalTextInput] multi=false expects v-model to be a string, got an array');
    }
  }

  const items = computed(() => {
    if (!props.multi) return [];
    return Array.isArray(inputValue.value) ? inputValue.value : [];
  });

  const canAdd = computed(() => items.value.length < props.maxItems);
  const canRemove = computed(() => items.value.length > props.minItems);

  function updateItem(index, value) {
    const next = [...items.value];
    next[index] = value;
    inputValue.value = next;
  }

  const inputRefs = ref([]);

  function setInputRef(el, index) {
    if (el) inputRefs.value[index] = el;
  }

  async function addItem() {
    if (!canAdd.value) return;
    inputValue.value = [...items.value, ''];
    await nextTick();
    const newIndex = items.value.length - 1;
    inputRefs.value[newIndex]?.focus();
  }

  function removeItem(index) {
    if (!canRemove.value || index === 0) return;
    const next = [...items.value];
    next.splice(index, 1);
    inputValue.value = next;
  }
</script>

<template>
  <div class="jwx-modal__form-control">
    <label
      v-if="props.labelText"
      :for="props.multi ? `${props.id}-0` : props.id"
      class="jwx-modal__control-label"
      :class="{ 'jwx-flex': props.required }"
    >
      {{ props.labelText }}
      <span
        v-if="props.required"
        class="jwx-modal__control-required"
        :data-valid="props.valid ? 'true' : null"
      >Required</span>
    </label>
    <div class="jwx-modal__input-group" :data-error="props.errorText ? 'true' : null">
      <template v-if="props.multi">
        <div
          v-for="(item, index) in items"
          :key="index"
          class="jwx-modal__input-wrapper"
        >
          <input
            :ref="(el) => setInputRef(el, index)"
            :value="item"
            @input="updateItem(index, $event.target.value)"
            type="text"
            :id="`${props.id}-${index}`"
            :name="`${props.name}[${index}]`"
            :placeholder="props.placeholder"
            class="jwx-modal__input"
          />
          <div
            v-if="(canRemove && index > 0) || (index === items.length - 1 && canAdd)"
            class="jwx-modal__input-actions"
          >
            <button
              v-if="canRemove && index > 0"
              type="button"
              class="jwx-modal__input-action"
              aria-label="Remove"
              @click="removeItem(index)"
            >
              <Icon name="minus" class="jwx-modal__input-action-icon" />
            </button>
            <button
              v-if="index === items.length - 1 && canAdd"
              type="button"
              class="jwx-modal__input-action"
              aria-label="Add"
              @click="addItem"
            >
              <Icon name="plus" class="jwx-modal__input-action-icon" />
            </button>
          </div>
        </div>
      </template>
      <div v-else class="jwx-modal__input-wrapper">
        <input
          v-model="inputValue"
          type="text"
          :id="props.id"
          :name="props.name"
          :placeholder="props.placeholder"
          class="jwx-modal__input"
        />
      </div>
    </div>
    <p v-if="props.errorText" class="jwx-modal__control-error">{{ props.errorText }}</p>
    <p v-else-if="props.subText" class="jwx-modal__control-subtext">{{ props.subText }}</p>
  </div>
</template>
